/**
 * Graph position utilities for stable node placement
 *
 * Provides deterministic initial positions and position caching
 * for consistent graph layouts across reloads.
 *
 * Positions are split into two categories:
 * - "pinned": the user explicitly dragged the node into place.
 * - "soft": everything else — either a previously cached (but not
 *   user-placed) position, or a deterministic seed position for a
 *   node we've never seen before.
 *
 * Cached positions never expire; a topology change (nodes added or
 * removed) is handled by filtering the cached map down to the
 * current node set rather than invalidating the whole cache.
 */

import type { ElementDefinition, Position } from 'cytoscape';

/**
 * Simple hash function for strings
 * Returns a number between 0 and 1
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Normalize to 0-1 range
  return Math.abs(hash) / 2147483647;
}

/**
 * Generate a second hash for y-coordinate (different seed)
 */
function hashStringAlt(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash) / 2147483647;
}

export interface NodePosition {
  x: number;
  y: number;
}

export interface PositionMap {
  [nodeId: string]: NodePosition;
}

export interface StoredPositions {
  positions: PositionMap;
  /** Node IDs the user explicitly dragged into place. Absent in legacy blobs — treat as []. */
  pinnedIds?: string[];
  timestamp: number;
}

export interface GraphPlacement {
  /** Position for EVERY node in nodeIds: cached where available, deterministic seed otherwise */
  positions: PositionMap;
  /** Subset of nodeIds the user explicitly dragged (pinned) — always have a cached position */
  pinnedIds: string[];
  /** Subset of nodeIds with no cached position (they got seed positions and need auto-layout) */
  unplacedIds: string[];
}

/**
 * Generate deterministic initial positions for nodes based on their IDs
 * Uses a hash-based approach to ensure the same node always gets the same position
 *
 * @param nodeIds - Array of node IDs
 * @param width - Width of the layout area (default 800)
 * @param height - Height of the layout area (default 600)
 * @param padding - Padding from edges (default 50)
 */
export function generateDeterministicPositions(
  nodeIds: string[],
  width = 800,
  height = 600,
  padding = 50
): PositionMap {
  const positions: PositionMap = {};
  const usableWidth = width - padding * 2;
  const usableHeight = height - padding * 2;

  nodeIds.forEach((id) => {
    const xHash = hashString(id);
    const yHash = hashStringAlt(id);

    positions[id] = {
      x: padding + xHash * usableWidth,
      y: padding + yHash * usableHeight,
    };
  });

  return positions;
}

/**
 * Apply positions to Cytoscape elements
 * Modifies elements in place to add position data
 */
export function applyPositionsToElements(
  elements: ElementDefinition[],
  positions: PositionMap
): ElementDefinition[] {
  return elements.map((element) => {
    // Only apply to nodes (elements without source/target are nodes)
    const isEdge = 'source' in element.data;
    if (isEdge) {
      return element;
    }

    const nodeId = element.data.id as string;

    // Check if we have a position for this node
    if (!(nodeId in positions)) {
      return element;
    }

    const position = positions[nodeId];
    return {
      ...element,
      position: { x: position.x, y: position.y },
    };
  });
}

/**
 * Storage key generator for position caching
 */
export function getPositionStorageKey(regionId: string, viewId = 'default'): string {
  return `netman:graph-positions:${regionId}:${viewId}`;
}

/**
 * Read the raw stored blob for a region/view, tolerating missing keys and
 * corrupt JSON (returns null in both cases, warning on the latter).
 */
function readStoredPositions(regionId: string, viewId: string): StoredPositions | null {
  const key = getPositionStorageKey(regionId, viewId);

  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    return JSON.parse(stored) as StoredPositions;
  } catch (e) {
    console.warn('Failed to load graph positions from localStorage:', e);
    return null;
  }
}

/**
 * Read just the pinned node IDs from storage, e.g. to compute an updated
 * pin set before writing back with {@link savePositions}.
 */
export function loadStoredPinnedIds(regionId: string, viewId = 'default'): string[] {
  return readStoredPositions(regionId, viewId)?.pinnedIds ?? [];
}

/**
 * Save positions to localStorage
 */
export function savePositions(
  regionId: string,
  positions: PositionMap,
  pinnedIds: string[],
  viewId = 'default'
): void {
  const key = getPositionStorageKey(regionId, viewId);
  const data: StoredPositions = {
    positions,
    pinnedIds,
    timestamp: Date.now(),
  };

  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.warn('Failed to save graph positions to localStorage:', e);
  }
}

/**
 * Compute the full placement for a set of nodes: cached positions merged
 * with deterministic seed positions for anything not yet cached, plus
 * which of those nodes are pinned vs. still need auto-layout.
 */
export function getPlacement(regionId: string, nodeIds: string[], viewId = 'default'): GraphPlacement {
  const stored = readStoredPositions(regionId, viewId);
  const storedPositions = stored?.positions ?? {};
  const storedPinnedIds = stored?.pinnedIds ?? [];

  const cached: PositionMap = {};
  const unplacedIds: string[] = [];

  for (const id of nodeIds) {
    if (id in storedPositions) {
      cached[id] = storedPositions[id];
    } else {
      unplacedIds.push(id);
    }
  }

  const pinnedIds = storedPinnedIds.filter((id) => id in cached);
  const seeded = generateDeterministicPositions(unplacedIds);

  return {
    positions: { ...seeded, ...cached },
    pinnedIds,
    unplacedIds,
  };
}

/**
 * Clear cached positions for a region
 */
export function clearPositions(regionId: string, viewId = 'default'): void {
  const key = getPositionStorageKey(regionId, viewId);
  localStorage.removeItem(key);
}

/**
 * Extract current positions from Cytoscape core instance
 */
export function extractPositionsFromCy(
  cy: { nodes: () => { forEach: (fn: (node: { id: () => string; position: () => Position }) => void) => void } }
): PositionMap {
  const positions: PositionMap = {};

  cy.nodes().forEach((node) => {
    const pos = node.position();
    positions[node.id()] = { x: pos.x, y: pos.y };
  });

  return positions;
}
